import { apiRequest, ApiError } from './http';
import type {
  ReferenceEntryDetail,
  ReferenceEntryItemPage,
  ReferenceEntrySummary,
  LibraryItem,
  LibraryType,
  ReferenceListResponse,
  ReferencePreview,
  ReferenceUsage,
  SaveReferenceEntryRequest,
  UpdateReferenceEntryRequest
} from './types';

const referencePath = '/api/manage/reference';

export async function listReferenceEntries(filters: {
  kind?: string;
  scope?: string;
  use?: string;
}): Promise<ReferenceListResponse> {
  const query = new URLSearchParams();
  if (filters.kind) query.set('kind', filters.kind);
  if (filters.scope) query.set('scope', filters.scope);
  if (filters.use) query.set('use', filters.use);
  const suffix = query.toString() ? `?${query}` : '';
  return apiRequest<ReferenceListResponse>(`${referencePath}${suffix}`);
}

export async function getReferenceEntry(scope: string, key: string): Promise<ReferenceEntryDetail> {
  return apiRequest<ReferenceEntryDetail>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`
  );
}

export async function createReferenceEntry(
  request: SaveReferenceEntryRequest
): Promise<ReferenceEntrySummary> {
  return apiRequest<ReferenceEntrySummary>(referencePath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
}

export async function updateReferenceEntry(
  scope: string,
  key: string,
  request: UpdateReferenceEntryRequest
): Promise<ReferenceEntrySummary> {
  return apiRequest<ReferenceEntrySummary>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    }
  );
}

export async function duplicateReferenceEntry(
  scope: string,
  key: string,
  newKey: string
): Promise<ReferenceEntrySummary> {
  return apiRequest<ReferenceEntrySummary>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/duplicate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newKey })
    }
  );
}

export async function listReferenceDependencies(scope: string, key: string): Promise<ReferenceUsage[]> {
  const response = await apiRequest<{ items?: ReferenceUsage[] }>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/dependencies`
  );
  return response.items ?? [];
}

export async function deleteReferenceEntry(scope: string, key: string, force = false): Promise<void> {
  const suffix = force ? '?force=true' : '';
  await apiRequest(`${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}${suffix}`, {
    method: 'DELETE'
  });
}

export function referenceContentUrl(scope: string, key: string, download = false): string {
  const suffix = download ? '?download=1' : '';
  return `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/content${suffix}`;
}

export function referenceItemContentUrl(
  scope: string,
  key: string,
  itemId: string,
  download = false
): string {
  const suffix = download ? '?download=1' : '';
  return `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/items/${encodeURIComponent(itemId)}/content${suffix}`;
}

export async function getReferencePreview(scope: string, key: string): Promise<ReferencePreview> {
  return apiRequest<ReferencePreview>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/preview`
  );
}

/** The wizard only knows a resolved reference KEY (`FormField.resolvedDefault`), not which scope
 * it lives in — template-scoped entries shadow global ones of the same key, so try that first,
 * mirroring ContentResolver.FromReferenceAsync's own fallback order. */
export async function getReferencePreviewForKey(
  templateId: string,
  key: string
): Promise<{ scope: string; preview: ReferencePreview } | null> {
  try {
    return { scope: templateId, preview: await getReferencePreview(templateId, key) };
  } catch (err: unknown) {
    if (!(err instanceof ApiError) || err.status !== 404) return null;
  }

  try {
    return { scope: 'global', preview: await getReferencePreview('global', key) };
  } catch {
    return null;
  }
}

export async function listReferenceItems(
  scope: string,
  key: string,
  skip: number,
  take: number
): Promise<ReferenceEntryItemPage> {
  const query = new URLSearchParams({ skip: String(skip), take: String(take) });
  return apiRequest<ReferenceEntryItemPage>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/items?${query}`
  );
}

export async function listLibraryItems(libraryType: LibraryType): Promise<LibraryItem[]> {
  const response = await apiRequest<{ items?: LibraryItem[] }>(
    `/api/manage/library/browse?library=${encodeURIComponent(libraryType)}`
  );
  return response.items ?? [];
}

export async function addReferenceItem(
  scope: string,
  key: string,
  request: { fileName: string; fileContentBase64: string }
): Promise<ReferenceEntryDetail> {
  return apiRequest<ReferenceEntryDetail>(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/items`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) }
  );
}

export async function removeReferenceItem(scope: string, key: string, itemId: string): Promise<void> {
  await apiRequest(
    `${referencePath}/${encodeURIComponent(scope)}/${encodeURIComponent(key)}/items/${encodeURIComponent(itemId)}`,
    { method: 'DELETE' }
  );
}
